import { useCallback, useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { adminApi } from "@/lib/api";
import { CheckCircle2, AlertTriangle, RefreshCw, PauseCircle, ArrowRight, MapPin } from "lucide-react";

/** Phase 9.1 — Ward Spot-Check Tool.
 * Side-by-side review: LEFT = live official source geometry, RIGHT = imported WYV copy.
 * A dataset stays non-verified until an admin explicitly verifies it here. */

const boundsOf = (geojson) => {
  try {
    const b = L.geoJSON(geojson).getBounds();
    return b.isValid() ? b : null;
  } catch {
    return null;
  }
};

const WardMap = ({ data, color, title, testId }) => {
  const bounds = useMemo(() => (data ? boundsOf(data) : null), [data]);
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">{title}</p>
      <div className="relative z-0 rounded-lg overflow-hidden border border-slate-200 h-[320px] sm:h-[420px]" data-testid={testId}>
        {data && bounds ? (
          <MapContainer bounds={bounds} scrollWheelZoom className="h-full w-full" attributionControl>
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                       url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <GeoJSON key={JSON.stringify(data.properties || {}).slice(0, 60) + (data.features?.length || 0)}
                     data={data}
                     style={() => ({ color, weight: 2, fillColor: color, fillOpacity: 0.1 })}
                     onEachFeature={(f, layer) => {
                       layer.bindTooltip(f.properties.ward_label || "?", { permanent: true, direction: "center", className: "ward-label-tip" });
                       layer.on({
                         mouseover: (e) => e.target.setStyle({ fillOpacity: 0.3 }),
                         mouseout: (e) => e.target.setStyle({ fillOpacity: 0.1 }),
                       });
                     }} />
          </MapContainer>
        ) : (
          <div className="h-full w-full flex items-center justify-center text-sm text-muted-foreground bg-slate-50">
            {data === undefined ? "Loading…" : "Unavailable"}
          </div>
        )}
      </div>
    </div>
  );
};

export default function SpotCheck() {
  const [queue, setQueue] = useState(null);
  const [idx, setIdx] = useState(0);
  const [detail, setDetail] = useState(null);
  const [source, setSource] = useState(undefined); // undefined=loading, null=failed
  const [sourceErr, setSourceErr] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [verifiedCount, setVerifiedCount] = useState(0);

  const loadQueue = useCallback(async (keepIdx = false) => {
    const r = await adminApi.get("/admin/coverage/spot-check/queue");
    setQueue(r.data.queue);
    setVerifiedCount(r.data.verified);
    if (!keepIdx) setIdx(0);
    return r.data.queue;
  }, []);

  useEffect(() => { loadQueue().catch(() => toast.error("Failed to load queue")); }, [loadQueue]);

  const current = queue && queue.length > 0 ? queue[Math.min(idx, queue.length - 1)] : null;

  useEffect(() => {
    if (!current) { setDetail(null); return; }
    setDetail(null); setSource(undefined); setSourceErr(null); setNotes("");
    adminApi.get(`/admin/coverage/spot-check/${current.municipality_slug}`)
      .then((r) => setDetail(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load detail"));
    adminApi.get(`/admin/coverage/spot-check/${current.municipality_slug}/source-preview`)
      .then((r) => setSource(r.data))
      .catch((e) => { setSource(null); setSourceErr(e.response?.data?.detail || "Live source fetch failed"); });
  }, [current?.municipality_slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (action) => {
    if (!current) return;
    if (action === "needs_correction" && !notes.trim()) {
      toast.error("Notes are required for Needs Correction");
      return;
    }
    setBusy(true);
    try {
      const r = await adminApi.post(`/admin/coverage/spot-check/${current.municipality_slug}/action`,
        { action, notes: notes.trim() || null });
      toast.success(action === "verify" ? `${current.municipality_name} marked VERIFIED` :
        action === "reimport" ? "Re-imported from official source" :
        action === "needs_correction" ? "Marked Needs Correction" : "Kept pending");
      if (action === "verify") {
        const q = await loadQueue(true);
        if (idx >= q.length) setIdx(Math.max(q.length - 1, 0));
      } else if (action === "reimport") {
        const slug = current.municipality_slug;
        const d = await adminApi.get(`/admin/coverage/spot-check/${slug}`);
        setDetail(d.data);
      }
      return r.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  if (!queue) return <div className="space-y-4"><Skeleton className="h-24" /><Skeleton className="h-96" /></div>;

  return (
    <div className="space-y-5" data-testid="spot-check-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground">Ward Spot-Check</h1>
          <p className="text-sm text-muted-foreground">
            Compare the live official source (left) against the imported WitnessYourVote copy (right),
            then verify or flag. Pending datasets are never presented publicly as verified.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200" data-testid="spot-check-verified-count">
            Verified: {verifiedCount}
          </Badge>
          <Badge variant="outline" className="bg-amber-50 text-amber-900 border-amber-300" data-testid="spot-check-remaining">
            Awaiting review: {queue.length}
          </Badge>
        </div>
      </div>

      {queue.length === 0 ? (
        <Card><CardContent className="p-8 text-center">
          <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto mb-2" />
          <p className="font-heading font-semibold text-foreground">Queue clear</p>
          <p className="text-sm text-muted-foreground">Every imported ward dataset has been reviewed.</p>
        </CardContent></Card>
      ) : !detail ? <Skeleton className="h-96" /> : (
        <>
          <Card data-testid="spot-check-meta">
            <CardContent className="p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-heading font-bold text-xl text-foreground inline-flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-accent" /> {detail.municipality.name}
                    <Badge variant="outline" className="text-[11px] bg-amber-50 text-amber-900 border-amber-300">{detail.status}</Badge>
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {detail.municipality.type}{detail.municipality.upper_tier ? ` · ${detail.municipality.upper_tier}` : ""} · {idx + 1} of {queue.length} in queue
                  </p>
                </div>
                <Button variant="outline" size="sm" disabled={idx >= queue.length - 1}
                        onClick={() => setIdx(idx + 1)} data-testid="spot-check-next">
                  Next Municipality <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 mt-4 text-xs">
                <div><p className="text-muted-foreground">Ward count</p><p className="font-medium text-foreground tabular-nums" data-testid="spot-check-ward-count">{detail.ward_count}</p></div>
                <div className="col-span-2"><p className="text-muted-foreground">Source</p><p className="font-medium text-foreground">{detail.source.name}</p></div>
                <div><p className="text-muted-foreground">Imported</p><p className="font-medium text-foreground">{detail.source.retrieved_at ? new Date(detail.source.retrieved_at).toLocaleDateString() : "—"}</p></div>
                <div className="col-span-2"><p className="text-muted-foreground">Geometry version (checksum)</p><p className="font-mono text-[11px] text-foreground">{detail.source.checksum || "—"}{detail.previous_checksum ? ` (prev: ${detail.previous_checksum})` : ""}</p></div>
                <div><p className="text-muted-foreground">Last checked</p><p className="font-medium text-foreground">{detail.source.last_checked_at ? new Date(detail.source.last_checked_at).toLocaleDateString() : "—"}</p></div>
                <div><p className="text-muted-foreground">Service URL</p><a href={detail.source.service_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all text-[10px]">{(detail.source.service_url || "").slice(0, 48)}…</a></div>
              </div>
              {source?.properties && (
                <div className="mt-3">
                  {source.properties.matches_import ? (
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[11px]" data-testid="spot-check-checksum-match">
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Live source matches imported geometry (checksum + {source.properties.ward_count} wards)
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-amber-50 text-amber-900 border-amber-300 text-[11px]" data-testid="spot-check-checksum-diff">
                      <AlertTriangle className="h-3 w-3 mr-1" /> Live source differs from import — inspect and consider re-import
                    </Badge>
                  )}
                </div>
              )}
              {detail.notes && <p className="text-[11px] text-muted-foreground mt-2">Notes: {detail.notes}</p>}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <WardMap data={source} color="#1e3a5f" title={`Official / source boundary (live fetch)`} testId="spot-check-source-map" />
              {sourceErr && <p className="text-[11px] text-amber-700 mt-1" data-testid="spot-check-source-error">{sourceErr}</p>}
            </div>
            <WardMap data={detail.imported_geojson} color="#0f766e" title="Imported WitnessYourVote boundary" testId="spot-check-imported-map" />
          </div>
          {detail.previous_geojson && (
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer text-primary hover:underline">Show previous geometry (before last re-import)</summary>
              <div className="mt-2 max-w-xl"><WardMap data={detail.previous_geojson} color="#b45309" title="Previous imported geometry" testId="spot-check-previous-map" /></div>
            </details>
          )}

          <Card data-testid="spot-check-actions">
            <CardContent className="p-5">
              <Textarea placeholder="Reviewer notes (required for Needs Correction)" rows={2} maxLength={2000}
                        value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="spot-check-notes" />
              <div className="flex flex-wrap gap-2 mt-3">
                <Button disabled={busy} onClick={() => act("verify")} className="bg-emerald-700 hover:bg-emerald-800" data-testid="spot-check-verify">
                  <CheckCircle2 className="h-4 w-4 mr-1.5" /> VERIFY
                </Button>
                <Button disabled={busy} variant="outline" onClick={() => act("needs_correction")} className="border-amber-300 text-amber-900 hover:bg-amber-50" data-testid="spot-check-needs-correction">
                  <AlertTriangle className="h-4 w-4 mr-1.5" /> NEEDS CORRECTION
                </Button>
                <Button disabled={busy} variant="outline" onClick={() => act("reimport")} data-testid="spot-check-reimport">
                  <RefreshCw className="h-4 w-4 mr-1.5" /> REIMPORT
                </Button>
                <Button disabled={busy} variant="ghost" onClick={async () => { await act("keep_pending"); setIdx(Math.min(idx + 1, queue.length - 1)); }} data-testid="spot-check-keep-pending">
                  <PauseCircle className="h-4 w-4 mr-1.5" /> KEEP PENDING
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
